import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import Popup from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import DataGrid, { Column, Editing, KeyboardNavigation, Scrolling, Selection, Sorting, FilterRow, ColumnChooser, Export, RangeRule } from 'devextreme-react/data-grid';
import { alert } from 'devextreme/ui/dialog';
import FormField from '../../../../../../dynamicFormBuilder/FormField';
import { MESSAGES } from '../../../../../../common/Messages';
import { DefaultShipmentItemsLayout, FIELD, LAYOUT_ID, LAYOUT_NAME } from './Layout';

// WMD0110 - "Default Shipment Item Details".
//
// Opens after a NEW shipment header is saved, when the header's profile carries default item lines
// (ALLOWITEMDETAILS 'S' or 'B') and the customer matches the profile's default header. The rows are
// the profile's default ('P') lines; the user ticks the ones to ship and may change the quantity and
// comment. OK creates one transaction detail per ticked row and the server then runs the stage change
// with the profile's own allocation methods - shown, read-only, in the Profile Methods group.
//
// Same shape as the replicate dialogs (ReplicateShipmentManualLot): a local array, cell editing, and a
// panel that follows the focused row. The panel is a dynamic form (see Layout.js).

const QUANTITY_FORMAT = { type: 'fixedPoint', precision: 2 };

// The only editable data column, and the read-only ones Tab skips over (Delphi grid columns 1-5).
const QUANTITY_FIELD = 'quantityOrder';
const READ_ONLY_FIELDS = new Set(['itemCode', 'itemDescription1', 'lotCode', 'itemAvailable', 'itemNetAvailable']);

// Where Enter already has a job, so it must not press OK: the grid cell being edited, the comment box,
// the grid's filter row, and any focused button.
const ENTER_OWNED_BY = '.dx-editor-cell, textarea, .dx-datagrid-filter-row, .dx-button';

const methodText = (isManual) =>
    (isManual ? MESSAGES.DEFAULT_SHIPMENT_ITEMS_MANUAL : MESSAGES.DEFAULT_SHIPMENT_ITEMS_AUTO);

// The values the panel shows: the focused row's lot and comment, and the profile's three methods.
const panelValues = (row, defaults) => ({
    [FIELD.LotDescription1]: row?.lotDescription1 ?? '',
    [FIELD.LotAvailable]: row?.lotAvailable ?? null,
    [FIELD.Comment]: row?.comment ?? '',
    [FIELD.LotMethod]: methodText(defaults?.isLotManual),
    [FIELD.SubLotMethod]: methodText(defaults?.isSubLotManual),
    [FIELD.LocationMethod]: methodText(defaults?.isLocationManual),
});

const DefaultShipmentItems = ({ visible, defaults, onClose, onOk, oidc }) => {
    const currentUser = oidc?.user;
    const dynamicFormSetting = currentUser?.profile?.DYNAMICFORMSETTING;

    const [rows, setRows] = useState([]);

    // Mirrors `rows`. saveEditData() below writes the open cell back through onRowUpdated, but a
    // setState from that handler is not visible to the same tick - reading state straight after the
    // await would still return the pre-edit value.
    const rowsRef = useRef([]);
    const applyRows = (next) => {
        rowsRef.current = next;
        setRows(next);
    };

    const dataGrid = useRef(null);
    const formRefs = useRef({});

    // The focused row is held by key, not as a copy: the Comment field writes back into `rows`, and a
    // copy would go stale the moment it did. Mirrored in a ref because FormField captures its change
    // handler once, on mount.
    const [focusedKey, setFocusedKey] = useState(null);
    const focusedKeyRef = useRef(null);
    const focusRow = (key) => {
        focusedKeyRef.current = key;
        setFocusedKey(key);
    };
    const focusedRow = rows.find(r => r.__key === focusedKey) ?? null;

    const [formFields, setFormFields] = useState(() => panelValues(null, defaults));

    // The ticked rows, through the grid's own multiple selection - its header checkbox is Delphi's
    // Select All / Unselect All. Each row's `selected` flag is kept in step, as that is what the server
    // reads.
    const [selectedKeys, setSelectedKeys] = useState([]);

    // Reloaded each time the dialog opens, so a previous run's edits are never reused. Every row starts
    // ticked, as in Delphi.
    useEffect(() => {
        if (!visible) return;
        const mapped = (defaults?.items || []).map((item, index) => ({ ...item, __key: index, selected: true }));
        applyRows(mapped);
        setSelectedKeys(mapped.map(r => r.__key));
        focusRow(mapped.length > 0 ? mapped[0].__key : null);
    }, [visible, defaults]);

    // The panel follows the focused row. Keyed on the row object, so typing a comment (which replaces
    // the row) re-syncs the panel to what was typed.
    useEffect(() => {
        setFormFields(panelValues(focusedRow, defaults));
    }, [focusedRow, defaults]);

    // Unticking zeroes Order Quantity and re-ticking does not bring it back - Delphi's
    // grdtemDetailsSELECTEDToggleClick sets QUANTITYORDER := 0 on uncheck only, and its Select All routes
    // every row through that same handler, so the header checkbox does the same.
    const onSelectionChanged = (e) => {
        const ticked = new Set(e.currentSelectedRowKeys);
        const unticked = new Set(e.currentDeselectedRowKeys);
        if (ticked.size > 0 || unticked.size > 0) {
            applyRows(rowsRef.current.map(r => {
                if (unticked.has(r.__key)) return { ...r, selected: false, quantityOrder: 0 };
                if (ticked.has(r.__key)) return { ...r, selected: true };
                return r;
            }));
        }
        setSelectedKeys(e.selectedRowKeys);
    };

    const onRowUpdating = (e) => {
        // A cleared cell is zero, which is what Delphi wrote for a blank quantity.
        if ('quantityOrder' in e.newData && e.newData.quantityOrder == null) {
            e.newData.quantityOrder = 0;
        }
    };

    // Writes the edited comment back to the focused row, so it travels with the rest of that line. Only
    // what the user typed - moving to another row changes the field's value programmatically, and that
    // is not an edit. Reads refs only: FormField keeps the first instance of this handler.
    const handleFieldValueChange = (name, value, e) => {
        if (name !== FIELD.Comment || !e?.event || focusedKeyRef.current === null) return;
        applyRows(rowsRef.current.map(r => (r.__key === focusedKeyRef.current ? { ...r, comment: value } : r)));
    };

    // Delphi disables OK while a row is being edited (BeforeEdit / AfterPost). Committing the open
    // edit first reaches the same outcome without taking the button away mid-type; an edit that fails
    // its own validation (a negative quantity) stays open for the user to fix.
    const handleOk = async () => {
        const grid = dataGrid.current?.instance();
        if (grid?.hasEditData()) await grid.saveEditData();
        if (grid?.hasEditData()) return;

        // The column's RangeRule should already have stopped this; the server refuses it as well.
        if (rowsRef.current.some(r => r.selected && Number(r.quantityOrder ?? 0) < 0)) {
            await alert(MESSAGES.DEFAULT_SHIPMENT_ITEMS_QUANTITY_NEGATIVE, MESSAGES.ERROR);
            return;
        }

        if (onOk) onOk(rowsRef.current.map(({ __key, ...row }) => row));
    };

    // Delphi's grdtemDetailsChangeColumn (uWMD0110.pas:250-256): landing on a read-only column moves the
    // cursor to Order Quantity, the only editable one, so Tab goes from quantity to quantity. Shift+Tab
    // from a quantity steps back to the previous row's quantity rather than bouncing in place.
    const onFocusedCellChanging = (e) => {
        const quantityIndex = e.columns.findIndex(c => c.dataField === QUANTITY_FIELD);
        const target = e.columns[e.newColumnIndex];
        if (quantityIndex < 0 || !target || !READ_ONLY_FIELDS.has(target.dataField)) return;

        const backFromQuantity = e.newRowIndex === e.prevRowIndex
            && e.prevColumnIndex === quantityIndex
            && e.newColumnIndex < quantityIndex;
        if (backFromQuantity) {
            if (e.newRowIndex === 0) return;
            e.newRowIndex -= 1;
        }
        e.newColumnIndex = quantityIndex;
    };

    // OK is Delphi's default button (btnOK Default = True), so Enter presses it - except where Enter
    // already means something: committing the cell being edited, a new line in the comment, the filter
    // row, or a focused button pressing itself. Captured so the grid does not also act on the key.
    const onKeyDownCapture = (e) => {
        if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.altKey) return;
        if (e.target.closest(ENTER_OWNED_BY)) return;
        e.preventDefault();
        e.stopPropagation();
        handleOk();
    };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={MESSAGES.DEFAULT_SHIPMENT_ITEMS_TITLE}
            container=".dx-viewport"
            width="85%"
            height="85%"
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} onKeyDownCapture={onKeyDownCapture}>
                <p style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '10px' }}>
                    {MESSAGES.DEFAULT_SHIPMENT_ITEMS_HELP}
                </p>

                <div style={{ flex: 1, minHeight: '150px' }}>
                    <DataGrid
                        ref={dataGrid}
                        dataSource={rows}
                        keyExpr="__key"
                        showBorders={true}
                        rowAlternationEnabled={true}
                        allowColumnResizing={true}
                        onExporting={(e) => {
                            if (window.handleDevExtremeDataGridExport) {
                                window.handleDevExtremeDataGridExport(e);
                            }
                        }}
                        columnAutoWidth={true}
                        height="100%"
                        onRowUpdating={onRowUpdating}
                        onRowUpdated={(e) => applyRows(rowsRef.current.map(r => (r.__key === e.key ? { ...r, ...e.data } : r)))}
                        onFocusedRowChanged={(e) => focusRow(e.row?.data?.__key ?? null)}
                        focusedRowEnabled={true}
                        focusedRowKey={focusedKey ?? undefined}
                        selectedRowKeys={selectedKeys}
                        onSelectionChanged={onSelectionChanged}
                        onFocusedCellChanging={onFocusedCellChanging}
                    >
                        <Editing mode="cell" allowUpdating={true} />
                        {/* Enter is OK (see onKeyDownCapture), so typing a digit is what starts editing. */}
                        <KeyboardNavigation editOnKeyPress={true} />
                        <Scrolling mode="virtual" />
                        {/* Checkboxes always shown: with "always" a row click does not change the
                            selection, so clicking into Order Quantity never unticks a row. */}
                        <Selection mode="multiple" showCheckBoxesMode="always" selectAllMode="allPages" />
                        <Sorting mode="multiple" />
                        <FilterRow visible={true} />
                        <ColumnChooser enabled={true} />
                        <Export enabled={true} />

                        {/* Keys only - carried so the server can re-read the profile line. */}
                        <Column dataField="rowId" visible={false} showInColumnChooser={false} allowEditing={false} />
                        <Column dataField="rItem" visible={false} showInColumnChooser={false} allowEditing={false} />
                        <Column dataField="rLot" visible={false} showInColumnChooser={false} allowEditing={false} />

                        <Column dataField="itemCode" caption={MESSAGES.DEFAULT_SHIPMENT_ITEMS_COL_ITEM} allowEditing={false} />
                        <Column dataField="itemDescription1" caption={MESSAGES.DEFAULT_SHIPMENT_ITEMS_COL_ITEM_DESCRIPTION} allowEditing={false} />
                        <Column dataField="lotCode" caption={MESSAGES.DEFAULT_SHIPMENT_ITEMS_COL_LOT} allowEditing={false} />
                        <Column dataField="itemAvailable" caption={MESSAGES.DEFAULT_SHIPMENT_ITEMS_COL_ITEM_AVAILABLE} dataType="number" alignment="right" allowEditing={false} format={QUANTITY_FORMAT} />
                        <Column dataField="itemNetAvailable" caption={MESSAGES.DEFAULT_SHIPMENT_ITEMS_COL_ITEM_NET_AVAILABLE} dataType="number" alignment="right" allowEditing={false} format={QUANTITY_FORMAT} />
                        {/* The only editable data column, as in Delphi. Blank is zero, zero is allowed, negative is refused. */}
                        <Column
                            dataField={QUANTITY_FIELD}
                            caption={MESSAGES.DEFAULT_SHIPMENT_ITEMS_COL_ORDER_QUANTITY}
                            dataType="number"
                            alignment="right"
                            allowEditing={true}
                            format={QUANTITY_FORMAT}
                            editorOptions={{ format: QUANTITY_FORMAT }}
                        >
                            <RangeRule min={0} message={MESSAGES.DEFAULT_SHIPMENT_ITEMS_QUANTITY_NEGATIVE} />
                        </Column>
                    </DataGrid>
                </div>

                <div style={{ paddingTop: '10px' }}>
                    {currentUser && (
                        <FormField
                            currrentUser={currentUser}
                            layoutId={LAYOUT_ID}
                            layoutName={LAYOUT_NAME}
                            defaultFormSchema={DefaultShipmentItemsLayout}
                            formFields={formFields}
                            setformFields={setFormFields}
                            handleFieldValueChange={handleFieldValueChange}
                            DYNAMICFORMSETTING={dynamicFormSetting}
                            formRefs={formRefs}
                        />
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '8px' }}>
                    <Button
                        text={MESSAGES.DEFAULT_SHIPMENT_ITEMS_OK}
                        type="default"
                        stylingMode="contained"
                        elementAttr={{ class: 'accordianButton' }}
                        onClick={handleOk}
                    />
                    <Button
                        text={MESSAGES.DEFAULT_SHIPMENT_ITEMS_CANCEL}
                        stylingMode="outlined"
                        onClick={onClose}
                    />
                </div>
            </div>
        </Popup>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(DefaultShipmentItems);
